import { Text, View } from "react-native";
import { Button } from "ui/gluestack/button";
import { useHandleResidentBio } from "../../hooks/useHandleResidentBio";
import { nextOfKinFields } from "app/constants/residentDetailsConstants";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { ModifyResidentRef } from "../interfaces";
import { ImageUpload } from "app/components/organisms/ImageUpload";
import { ResidentInfoInputGroup } from "../../organisms/ResidentInfoInputGroup";
import { NextOfKinGroup } from "../../organisms/NextOfKinGroup";
import { residentProfileMimeTypes } from "app/constants/allowedExtensionsConstants";
import { Controller } from "react-hook-form";
import { useGetCountriesQuery } from "app/services/api/countriesApi";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "ui/gluestack/checkbox";
import { CheckIcon } from "ui/gluestack/icon";
import { useCreateResidentFields } from "../../hooks/useCreateResidentFields";

const ResidentBioFormTemplate = forwardRef<ModifyResidentRef, {}>((_, ref) => {
  const {
    control,
    submitForm,
    appendNextOfKin,
    fields,
    remove,
    shouldShowOutsidePassDetails,
    shouldShowMaritalDetails,
  } = useHandleResidentBio();

  const { inputFields } = useCreateResidentFields({
    allowMaritalDescription: shouldShowMaritalDetails,
  });

  useImperativeHandle(
    ref,
    () => ({
      continue: () => submitForm(),
    }),
    [submitForm],
  );

  return (
    <View>
      <View className="md:items-start items-center pb-12">
        <Controller
          control={control}
          name="profile_image"
          defaultValue={undefined}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <ImageUpload
                value={value}
                onChange={(file) => {
                  onChange(file);
                }}
                onRemove={() => {
                  onChange(undefined);
                }}
                allowedExtensions={residentProfileMimeTypes}
                errorMessage={error}
              />
            );
          }}
        />
      </View>

      <ResidentInfoInputGroup
        fields={inputFields}
        control={control}
      ></ResidentInfoInputGroup>

      <FormControlledInputTextArea
        id="outside_pass_status_details"
        name="outside_pass_status_details"
        control={control}
        label="Outside Pass Status Details"
        placeholder="Enter details if outside pass status is set to Other"
        className="text-gray-500 mt-8"
        isRequired={shouldShowOutsidePassDetails}
        isDisabled={!shouldShowOutsidePassDetails}
        rules={{
          validate: shouldShowOutsidePassDetails
            ? (value) =>
                !value ? "Outside Pass Status Details is required" : true
            : undefined,
        }}
      />

      <View className="mt-8">
        <Controller
          control={control}
          name="photo_consent"
          render={({ field: { value, onChange } }) => (
            <Checkbox
              isChecked={!!value}
              onChange={(checked: boolean) => onChange(checked)}
              size="sm"
              className="mt-4"
              value={value ? "checked" : "unchecked"}
            >
              <CheckboxIndicator>
                <CheckboxIcon as={CheckIcon} />
              </CheckboxIndicator>
              <CheckboxLabel>Photography/Filming Consent</CheckboxLabel>
            </Checkbox>
          )}
        />
      </View>

      <View className="border border-border-200 w-full my-16" />

      <Text className="text-lg font-medium text-typography-800">
        Next of Kin
      </Text>
      {fields.map((item, index) => (
        <NextOfKinGroup
          key={item.id}
          fields={nextOfKinFields}
          control={control}
          remove={remove}
          index={index}
        />
      ))}
      <View className="mt-4 md:w-36">
        <Button
          className="bg-secondary-0 text-nowrap"
          onPress={appendNextOfKin}
          action="secondary"
        >
          <Text className="text-typography-900">Add another +</Text>
        </Button>
      </View>
    </View>
  );
});

ResidentBioFormTemplate.displayName = "ResidentBioFormTemplate";
export { ResidentBioFormTemplate };
